import React, { useCallback, useRef } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import ERATECertificate from "../../assets/image/erate.jpg";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  align-self: start;

  @media (max-width: ${breakpoints.tablet}px) {
    max-width: 380px;
    justify-self: center;
    width: 100%;
  }

  @media (max-width: ${breakpoints.phone}px) {
    max-width: 100%;
  }
`;

const Header = styled.div`
  font-size: 1rem;
  font-weight: 700;
  padding: 10px 16px;
  color: #ffffff;
  background-color: ${({ colors }) => colors?.componentHeaderBackground || "#2C4960"};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 14px 16px;
  background-color: ${({ colors }) => colors?.cardBackground || "#ffffff"};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.li`
  font-size: 0.9rem;

  a {
    color: #333;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    font-weight: 700;
    color: #d73a49;
  }
`;

const HiddenCanvas = styled.canvas`
  display: none;
`;

const ResourcesDownloadCCSS = ({ colors, links }) => {
  const firstName = useSelector((state) => state.auth.user?.first_name || "");
  const lastName = useSelector((state) => state.auth.user?.last_name || "");
  const canvasRef = useRef(null);

  const handleCertificateDownload = useCallback(
    (event) => {
      event.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const blankCertificate = new Image();
      blankCertificate.src = ERATECertificate;

      blankCertificate.onload = () => {
        canvas.width = blankCertificate.width;
        canvas.height = blankCertificate.height;

        ctx.drawImage(blankCertificate, 0, 0);

        const userName = `${firstName} ${lastName}`.trim();
        ctx.font = "bold 50px Arial";
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.fillText(userName, canvas.width / 2, canvas.height / 2);

        const currentDate = new Date().toLocaleDateString();
        ctx.font = "20px Arial";
        ctx.textAlign = "right";
        ctx.fillText(currentDate, canvas.width - 120, canvas.height - 170);

        const certificateImage = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [canvas.width, canvas.height],
        });

        pdf.addImage(certificateImage, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${userName || "Educator"}_Certificate.pdf`);
      };
    },
    [firstName, lastName]
  );

  return (
    <Panel>
      <Header colors={colors}>Resource Download</Header>
      <List colors={colors}>
        {links?.map(({ label, href, isCertificate }) => (
          <Item key={href}>
            <a
              href={href}
              target={isCertificate ? undefined : "_blank"}
              rel={isCertificate ? undefined : "noopener noreferrer"}
              onClick={isCertificate ? handleCertificateDownload : undefined}
            >
              {label}
            </a>
          </Item>
        ))}
      </List>
      <HiddenCanvas ref={canvasRef} />
    </Panel>
  );
};

export default ResourcesDownloadCCSS;
